import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface FullPageLoaderProps {
  message?: string;
  fullViewport?: boolean;
}

interface LoaderContainerParams {
  fullViewport: boolean;
}

/**
 * Determines positioning strategy for the loader overlay.
 * Fixed: covers entire viewport (for global loading)
 * Absolute: covers parent container (for component-level loading)
 */
const getContainerClasses = ({ fullViewport }: LoaderContainerParams): string => {
  return cn(
    'inset-0 z-50 flex items-center justify-center bg-background',
    fullViewport ? 'fixed' : 'absolute'
  );
};

/**
 * A versatile full-screen or container-scoped loading overlay component.
 * Features an animated spinner with optional custom message text.
 *
 * **Key Features:**
 * - Two positioning modes: viewport-fixed or container-absolute
 * - Customizable loading messages with i18n support
 * - Accessible design with proper contrast and animations
 * - Responsive layout that works across all screen sizes
 * - High z-index ensures it appears above other content
 *
 * **Use Cases:**
 * - Global app initialization loading states
 * - Page transitions and route changes
 * - Large data fetch operations
 * - Component-level loading overlays
 * - Form submission processing states
 *
 * @example
 * ```tsx
 * // Global loading overlay (covers entire viewport)
 * <FullPageLoader
 *   message="Loading application..."
 *   fullViewport={true}
 * />
 *
 * // Container-scoped loading (covers parent container)
 * <div className="relative h-96">
 *   <FullPageLoader
 *     message="Loading project details..."
 *     fullViewport={false}
 *   />
 * </div>
 *
 * // With internationalized message
 * <FullPageLoader
 *   message={t('loading.fetchingData')}
 * />
 *
 * // Minimal usage (uses default message)
 * <FullPageLoader />
 *
 * // Page-level loading state
 * function ProjectDetailsPage() {
 *   const { data, isLoading } = useQuery(['project', id], fetchProject);
 *
 *   if (isLoading) {
 *     return (
 *       <FullPageLoader
 *         message="Loading project details..."
 *         fullViewport={false}
 *       />
 *     );
 *   }
 *
 *   return <div>{data.name}</div>;
 * }
 *
 * // Component overlay loading
 * function DataTable({ isRefreshing }: { isRefreshing: boolean }) {
 *   return (
 *     <div className="relative">
 *       <table>...</table>
 *       {isRefreshing && (
 *         <FullPageLoader
 *           message="Refreshing data..."
 *           fullViewport={false}
 *         />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function FullPageLoader({ message, fullViewport = true }: FullPageLoaderProps) {
  const { t } = useTranslation('common');
  const containerClasses = getContainerClasses({ fullViewport });
  // Fallback to default loading message if none provided
  const displayMessage = message || t('loading');

  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        {displayMessage && (
          <p className="animate-pulse text-sm text-muted-foreground">{displayMessage}</p>
        )}
      </div>
    </div>
  );
}
